import { ChangeEvent, useState } from 'react';
import { Button, Column, Row, Typography, InputTextField } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { useValidatePromoCode } from '~/domain/PromoCode';
import { SolidCheckCircleIcon } from '@tempmee/react-icons';

type TAppliedPromoState = { code: string; discount: string; finalPrice: number };

interface IPromoCodeSectionProps {
  basePrice: number;
  onApply: (code: string, finalPrice: number) => void;
  onRemove?: () => void;
}

export const PromoCodeSection = ({ basePrice, onApply, onRemove }: IPromoCodeSectionProps) => {
  const [inputCode, setInputCode] = useState<string>('');
  const [appliedState, setAppliedState] = useState<TAppliedPromoState | null>(null);
  const [pendingRestore, setPendingRestore] = useState<TAppliedPromoState | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { mutate, isPending } = useValidatePromoCode();

  const handleApply = () => {
    if (!inputCode.trim()) {
      setError('Invalid code. Try again.');
      return;
    }

    mutate(
      { code: inputCode, basePrice: basePrice.toFixed(2) },
      {
        onSuccess: (data) => {
          if (data.valid) {
            setError(null);
            const finalPrice = parseFloat(data.finalPrice!);
            setAppliedState({ code: inputCode, discount: data.discount!, finalPrice });
            setPendingRestore(null);
            onApply(inputCode, finalPrice);
          } else {
            setError('Invalid code. Try again.');
          }
        },
        onError: () => {
          setError('Invalid code. Try again.');
        },
      },
    );
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputCode(e.target.value);
    if (error) setError(null);
    if (appliedState) {
      setPendingRestore(appliedState);
      setAppliedState(null);
      onRemove?.();
    }
  };

  const handleRemove = () => {
    setAppliedState(null);
    setPendingRestore(null);
    setInputCode('');
    onRemove?.();
  };

  const handleBlur = () => {
    if (!inputCode.trim() && pendingRestore) {
      setAppliedState(pendingRestore);
      setInputCode(pendingRestore.code);
      setPendingRestore(null);
      onApply(pendingRestore.code, pendingRestore.finalPrice);
    }
  };

  return (
    <Column gap={3}>
      <Typography as="span" size="body_small" bold color={colors.neutral[600]}>
        Promo Code
      </Typography>
      <Column gap={1}>
        <Row gap={2} align="center">
          <InputTextField
            placeholder="Enter code"
            value={inputCode}
            onChange={handleInputChange}
            onBlur={handleBlur}
            disabled={isPending}
            isError={!!error}
            style={{ flex: 1 }}
          />
          <Button
            onClick={appliedState && !pendingRestore ? handleRemove : handleApply}
            isLoading={isPending}
            disabled={!(appliedState && !pendingRestore) && (!inputCode.trim() || isPending)}
            size="sm"
            variant="secondary"
            style={{ height: 30, width: 120 }}
          >
            {appliedState && !pendingRestore ? 'Remove' : 'Apply'}
          </Button>
        </Row>
        {appliedState && (
          <Row gap={2} align="center">
            <SolidCheckCircleIcon color={colors.success[500]} />
            <Typography as="span" size="body_small" bold color={colors.success[500]}>
              Promo Code Applied{' '}
              <Typography as="span">
                {`(-$${parseFloat(appliedState.discount).toFixed(2)})`}
              </Typography>
            </Typography>
          </Row>
        )}
        {!!error && (
          <Typography color={colors.error[500]} size="caption">
            Invalid code. Try again
          </Typography>
        )}
      </Column>
    </Column>
  );
};
